import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { usersApi } from '../../../../api/users';
import { useDebouncedValue } from '../../../../lib/useDebouncedValue';
import { AsyncCombobox, AsyncComboboxOption } from '../async-combobox';
import { UserComboboxProps, UserComboboxUser } from './interface';

const EMPTY_USER_VALUE = '__none__';

function toUserOption(user: UserComboboxUser): AsyncComboboxOption {
  return { value: user.id, label: user.name, description: user.email };
}

export function UserCombobox({
  value,
  onValueChange,
  selectedUser,
  allowEmpty = false,
  emptyLabel = 'None',
  excludeProjectId,
  placeholder = 'Select a user...',
  emptyMessage = 'No users found.',
  disabled = false,
  className,
}: UserComboboxProps) {
  const [userSearch, setUserSearch] = useState('');
  // Keeps the trigger label when the chosen user drops out of the current search results.
  const [lastSelected, setLastSelected] = useState<AsyncComboboxOption>();
  const debouncedUserSearch = useDebouncedValue(userSearch.trim(), 300);
  const isSearchPending = userSearch.trim() !== debouncedUserSearch;

  const usersQuery = useInfiniteQuery({
    queryKey: ['user-options', debouncedUserSearch, excludeProjectId],
    queryFn: ({ pageParam, signal }) => usersApi.list({
      page: pageParam,
      limit: 20,
      name: debouncedUserSearch || undefined,
      excludeProjectId,
      sortBy: 'name',
    }, signal).then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.page < lastPage.meta.totalPages
      ? lastPage.meta.page + 1
      : undefined,
  });

  const loadedOptions = usersQuery.data?.pages.flatMap((page) => page.data.map(toUserOption)) ?? [];
  const options = isSearchPending
    ? []
    : [
      ...(allowEmpty ? [{ value: EMPTY_USER_VALUE, label: emptyLabel }] : []),
      ...loadedOptions,
    ];

  return (
    <AsyncCombobox
      value={value}
      options={options}
      selectedOption={[selectedUser && toUserOption(selectedUser), lastSelected].find((option) => option?.value === value) ?? undefined}
      search={userSearch}
      onSearchChange={setUserSearch}
      onValueChange={(nextValue) => {
        setLastSelected(options.find((option) => option.value === nextValue));
        onValueChange(nextValue === EMPTY_USER_VALUE ? '' : nextValue);
      }}
      onLoadMore={() => usersQuery.fetchNextPage()}
      onRetry={() => usersQuery.refetch()}
      placeholder={placeholder}
      searchPlaceholder="Search users by name..."
      emptyMessage={emptyMessage}
      isLoading={isSearchPending || usersQuery.isLoading}
      isFetching={isSearchPending || usersQuery.isFetching}
      isFetchingMore={usersQuery.isFetchingNextPage}
      hasMore={usersQuery.hasNextPage}
      hasError={usersQuery.isError}
      disabled={disabled}
      className={className}
    />
  );
}

export type { UserComboboxProps, UserComboboxUser } from './interface';
